<!-- components/sidebar/AppSidebar.vue - Fixed sidebar with proper navigation filtering -->
<template>
  <VaSidebar v-model="writableVisible" class="app-sidebar" :width="sidebarWidth" :color="color" gradient>
    <!-- User Info Section -->
    <div class="pa-3">
      <div class="user-info-section pa-3">
        <div class="flex items-center gap-3">
          <VaAvatar color="primary" size="medium">
            {{ getUserInitials }}
          </VaAvatar>
          <div class="flex-1">
            <div class="font-semibold text-sm">{{ userName }}</div>
            <div class="text-xs opacity-70">{{ userRole }}</div>
          </div>
        </div>
      </div>
    </div>

    <VaDivider />

    <!-- Navigation Menu -->
    <div class="navigation-menu">
      <template v-for="(route, idx) in filteredRoutes" :key="`route-${idx}`">
        <!-- Routes without children - Simple navigation item -->
        <VaSidebarItem
          v-if="!route.children || route.children.length === 0"
          :active="isActiveRoute(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          :aria-label="`Navigate to ${t(route.displayName)}`"
          hover-opacity="0.10"
          class="sidebar-item parent-item standalone"
          @click="handleRouteClick(route)"
        >
          <VaSidebarItemContent class="py-3 px-3">
            <VaIcon v-if="route.meta.icon" :name="route.meta.icon" :color="iconColor(route)" size="20px" class="mr-3" />
            <VaSidebarItemTitle class="leading-6">
              {{ t(route.displayName) }}
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>

        <!-- Routes with children - Expandable parent with children -->
        <div v-else class="parent-with-children">
          <!-- Parent Item (Clickable to expand/collapse) -->
          <VaSidebarItem
            class="sidebar-item parent-item has-children"
            :active="isParentActive(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            hover-opacity="0.10"
            @click="toggleExpanded(idx)"
          >
            <VaSidebarItemContent class="py-3 px-3">
              <VaIcon
                v-if="route.meta.icon"
                :name="route.meta.icon"
                :color="iconColor(route)"
                size="20px"
                class="mr-3"
              />
              <VaSidebarItemTitle class="leading-6 flex-1">
                {{ t(route.displayName) }}
              </VaSidebarItemTitle>
              <VaIcon
                :name="expandedStates[idx] ? 'expand_less' : 'expand_more'"
                size="20px"
                :color="iconColor(route)"
                class="expand-icon"
                :class="{ 'rotate-180': expandedStates[idx] }"
              />
            </VaSidebarItemContent>
          </VaSidebarItem>

          <!-- Child routes (Collapsible) -->
          <Transition name="slide">
            <div v-show="expandedStates[idx]" class="child-routes">
              <VaSidebarItem
                v-for="(childRoute, childIdx) in route.children"
                :key="`child-${idx}-${childIdx}`"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Navigate to ${t(childRoute.displayName)}`"
                hover-opacity="0.10"
                class="sidebar-item child-item"
                @click.stop="handleRouteClick(childRoute)"
              >
                <VaSidebarItemContent class="py-2 px-3">
                  <VaIcon
                    v-if="childRoute.meta?.icon"
                    :name="childRoute.meta.icon"
                    :color="isActiveChildRoute(childRoute) ? 'primary' : 'secondary'"
                    size="16px"
                    class="mr-2 ml-8"
                  />
                  <span v-else class="ml-10"></span>
                  <VaSidebarItemTitle class="leading-5 text-sm">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </Transition>
        </div>
      </template>
    </div>

    <!-- Bottom Section -->
    <template #bottom>
      <VaDivider />
      <div class="pa-3">
        <div v-if="userStore.user?.entityLookUpList && userStore.user.entityLookUpList.length > 1" class="mb-3">
          <VaSelect
            v-model="selectedEntityId"
            :options="entityOptions"
            label="Entity"
            text-by="text"
            value-by="value"
            class="w-full"
            size="small"
            @update:modelValue="handleEntityChange"
          />
        </div>
        <VaButton preset="secondary" size="small" class="w-full" @click="logout">
          <VaIcon name="logout" size="16px" class="mr-1" />
          Logout
        </VaButton>
      </div>
    </template>
  </VaSidebar>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useMsal } from 'vue3-msal-plugin'
import { useUserStore } from '../../stores/user'

// Navigation route interface
interface INavigationRoute {
  name: string
  displayName: string
  meta: {
    icon?: string
    permissions?: string[]
    requiresAuth?: boolean
    isPowerBI?: boolean
    defaultCodename?: string
    defaultName?: string
  }
  children?: INavigationRoute[]
}

// Props and Emits
const props = defineProps<{
  visible?: boolean
  mobile?: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

// Composables
const { getColor, colorToRgba } = useColors()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { instance } = useMsal()
const userStore = useUserStore()

// State
const expandedStates = reactive<Record<number, boolean>>({})
const selectedEntityId = ref<number>(userStore.user?.lastSelectedEntity?.id || 0)

// Computed properties
const writableVisible = computed({
  get: () => props.visible ?? true,
  set: (v: boolean) => emit('update:visible', v),
})

const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
const color = computed(() => getColor('background-secondary'))
const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

// User info
const getUserInitials = computed(() => {
  const name = userStore.displayName || 'User'
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
})

const userName = computed(() => userStore.displayName || 'User')
const userRole = computed(() => userStore.userRole || 'Role')

// Entity options for dropdown
const entityOptions = computed(() => {
  const entities = userStore.user?.entityLookUpList || []
  return entities.map((entity) => ({
    text: entity.entity_name,
    value: entity.id,
  }))
})

// Define navigation routes with PowerBI configuration
const navigationRoutes: INavigationRoute[] = [
  {
    name: 'dashboard',
    displayName: 'menu.dashboard',
    meta: {
      icon: 'dashboard',
      // requiresAuth: false,
    },
  },
  {
    name: 'user-dashboard',
    displayName: 'menu.bireports',
    meta: {
      icon: 'folder_shared',
      // permissions: ['admin_access'],
      // requiresAuth: false,
      isPowerBI: true,
      defaultCodename: 'user-dashboard',
      defaultName: 'user-Dashboard',
    },
  },
  {
    name: 'hr-dashboard',
    displayName: 'menu.execheadreports',
    meta: {
      icon: 'leaderboard',
      // permissions: ['admin_access'],
      // requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'hr-dashboard',
      defaultName: 'HR-Dashboard',
    },
  },
  {
    name: 'leads-dashboard',
    displayName: 'menu.leadsreports',
    meta: {
      icon: 'group_work',
      // permissions: ['admin_access'],
      // requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'leads-dashboard',
      defaultName: 'Leads-Dashboard',
    },
  },
  {
    name: 'users',
    displayName: 'User Management',
    meta: {
      icon: 'group',
      permissions: ['admin_access'],
      requiresAuth: true,
    },
  },
  {
    name: 'preferences',
    displayName: 'Preferences',
    meta: {
      icon: 'settings',
      requiresAuth: true,
    },
  },
]

// Filter routes based on permissions
const filteredRoutes = computed(() => {
  const isAuthenticated = userStore.isAuthenticated
  const isSuperUser = userStore.user?.isSuperUser || false

  return navigationRoutes
    .map((route) => {
      // Always show public routes (requiresAuth: false)
      if (!route.meta.requiresAuth) {
        return route
      }

      // Check authentication for protected routes
      if (!isAuthenticated) {
        return null
      }

      // Super users see everything
      if (isSuperUser) {
        return route
      }

      // Check permissions for the parent route
      if (route.meta.permissions && route.meta.permissions.length > 0) {
        const hasPermission = userStore.hasAnyPermission(route.meta.permissions)
        if (!hasPermission) {
          return null
        }
      }

      // Filter children based on permissions
      if (route.children) {
        const filteredChildren = route.children.filter((child) => {
          // Keep public children
          if (!child.meta?.requiresAuth) {
            return true
          }

          // Super users see all children
          if (isSuperUser) {
            return true
          }

          // Check permissions for protected children
          if (child.meta?.permissions) {
            return userStore.hasAnyPermission(child.meta.permissions)
          }

          // If no specific permissions required, just need to be authenticated
          return isAuthenticated
        })

        // Always return the parent with filtered children
        return { ...route, children: filteredChildren }
      }

      return route
    })
    .filter(Boolean) as INavigationRoute[]
})

// Navigation helpers
const isActiveRoute = (routeItem: INavigationRoute) => {
  return route.name === routeItem.name
}

const isActiveChildRoute = (child: INavigationRoute) => {
  return route.name === child.name
}

const isParentActive = (parent: INavigationRoute) => {
  // Parent is active if it's the current route or has an active child
  if (isActiveRoute(parent)) {
    return true
  }
  if (parent.children) {
    return parent.children.some((child) => isActiveChildRoute(child))
  }
  return false
}

const toggleExpanded = (idx: number) => {
  expandedStates[idx] = !expandedStates[idx]
}

const iconColor = (routeItem: INavigationRoute) => {
  if (isActiveRoute(routeItem)) return 'primary'
  if (routeItem.children && routeItem.children.some((child) => isActiveChildRoute(child))) return 'primary'
  return 'secondary'
}

const textColor = (routeItem: INavigationRoute) => {
  if (isActiveRoute(routeItem)) return 'primary'
  if (routeItem.children && routeItem.children.some((child) => isActiveChildRoute(child))) return 'primary'
  return 'textPrimary'
}

// Initialize expanded states
const initializeExpandedStates = () => {
  filteredRoutes.value.forEach((route, idx) => {
    if (route.children && route.children.length > 0) {
      // Check if not already set
      if (expandedStates[idx] === undefined) {
        // Auto-expand if it has an active child or if it's the calculators menu
        const hasActiveChild = route.children.some((child) => isActiveChildRoute(child))
        const isCalculators = route.name === 'calculators'
        expandedStates[idx] = hasActiveChild || isCalculators
      }
    }
  })
}

// Handle navigation clicks
const handleRouteClick = (routeItem: INavigationRoute) => {
  // Special handling for PowerBI routes
  if (routeItem.meta.isPowerBI) {
    const codename = routeItem.meta.defaultCodename || 'report'
    const name = routeItem.meta.defaultName || 'Report'

    router.push({
      name: routeItem.name,
      params: {
        codename: codename,
        name: name,
      },
    })
  } else {
    // Regular navigation
    router.push({ name: routeItem.name })
  }
}

// Handle entity change
const handleEntityChange = async (entityId: number) => {
  await userStore.switchEntity(entityId)
  // Refresh the current page to load new entity data
  router.go(0)
}

// Logout
const logout = async () => {
  try {
    userStore.clearUser()

    // MSAL logout
    await instance.logoutPopup({
      mainWindowRedirectUri: '/',
    })

    router.push({ name: 'login' })
  } catch (error) {
    console.error('Logout error:', error)
    // Even if MSAL fails, still redirect to login
    router.push({ name: 'login' })
  }
}

// Watchers
watch(
  () => route.fullPath,
  () => {
    // Don't reset the expanded states on route change
    // Just update if there's a new active child
    filteredRoutes.value.forEach((route, idx) => {
      if (route.children && route.children.length > 0) {
        const hasActiveChild = route.children.some((child) => isActiveChildRoute(child))
        if (hasActiveChild) {
          expandedStates[idx] = true
        }
      }
    })
  },
)

watch(
  () => userStore.userPermissions,
  () => {
    initializeExpandedStates()
  },
  { deep: true },
)

// Lifecycle
onMounted(() => {
  initializeExpandedStates()
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  :deep(.va-sidebar__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .user-info-section {
    background: rgba(var(--va-primary), 0.05);
    border-radius: 8px;
  }

  .navigation-menu {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 15px 5px;
  }

  .parent-with-children {
    margin-bottom: 2px;
  }

  .sidebar-item {
    transition: all 0.2s ease;
    cursor: pointer;
    margin: 2px 8px;
    border-radius: 8px;

    &.parent-item {
      font-weight: 500;

      &.has-children {
        :deep(.va-sidebar-item__content) {
          position: relative;
          user-select: none;
        }
      }

      &.standalone {
        margin-bottom: 4px;
      }
    }

    &.child-item {
      margin: 2px 0;
      border-radius: 8px;

      :deep(.va-sidebar-item__content) {
        font-size: 0.875rem;
      }
    }

    &:hover {
      background: rgba(var(--va-primary), 0.05);
    }

    :deep(.va-sidebar-item__content) {
      display: flex;
      align-items: center;
    }
  }

  .child-routes {
    background: rgba(var(--va-background-element), 0.3);
    border-left: 2px solid rgba(var(--va-primary), 0.15);
    margin-left: 1.25rem;
    margin-top: 2px;
    margin-bottom: 4px;
    padding: 4px 0;
    overflow: hidden;
  }

  .expand-icon {
    transition: transform 0.3s ease;

    &.rotate-180 {
      transform: rotate(180deg);
    }
  }

  // Smooth slide transition
  .slide-enter-active,
  .slide-leave-active {
    transition: all 0.3s ease;
  }

  .slide-enter-from {
    opacity: 0;
    transform: translateY(-10px);
  }

  .slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
